import React from 'react'

import { Features } from 'generated/featureDefinitions'
import {
  AttendanceSidepanelCompensationBlockFragment,
  BankOfHoursBreakdownPanelDocument,
  BulkCreateAttendanceTimeBlockCompensationMutation,
} from 'generated/resources/graphql'

type AttendanceManualCompensation = NonNullable<
  NonNullable<
    BulkCreateAttendanceTimeBlockCompensationMutation['attendanceMutations']['bulkCreateTimeBlockSupplementAttendanceManualCompensation']
  >['manualCompensation']
>
import { Moment } from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'

import CompensationResponse from 'modules/attendance/components/ClockIn/EmployeeTimeTrackingBreakdown/Compensation/Modals/CompensationResponse'
import CreateTimeBlockCompensationWizard from 'modules/attendance/components/ClockIn/EmployeeTimeTrackingBreakdown/Compensation/Modals/CreateTimeBlockCompensationWizard'
import CreateTimeBlockSupplementWizard from 'modules/attendance/components/ClockIn/EmployeeTimeTrackingBreakdown/Compensation/Modals/CreateTimeBlockSupplementWizard'
import { useAttendanceFeatures } from 'modules/attendance/lib/attendanceFeaturesContext'
import { useTimesheetData } from 'modules/attendance/lib/timesheetDataContext'

type Props = {
  employeeId: number
  timeSheetDate: Moment
  openModal: boolean
  setOpenModal: (open: boolean) => void
  timeSheetData?: ReturnType<typeof useTimesheetData> | undefined
  skipQuery?: boolean
  onClose?: () => void
  selectedCompensationBlocks?: AttendanceSidepanelCompensationBlockFragment[]
}

const CompensationModalWrapper = ({
  employeeId,
  timeSheetDate,
  openModal,
  setOpenModal,
  timeSheetData,
  skipQuery = false,
  onClose,
  selectedCompensationBlocks = [],
}: Props) => {
  const [openResponseModal, setOpenResponseModal] = React.useState(false)

  const { featureAvailability: hasCompensations } = useAttendanceFeatures(
    Features.compensations_management
  )

  const { featureAvailability: hasCompensationFlowV2 } = useAttendanceFeatures(
    'dev_time_tracking_compensation_flow_v2'
  )

  const [responseData, setResponseData] = React.useState<
    AttendanceManualCompensation | undefined | null
  >()

  const { timeBlocksBalance, loading: isLoading } = timeSheetData ?? {}

  const getResponseData = (response: AttendanceManualCompensation | undefined | null) => {
    setResponseData(response)
    if (response) {
      setOpenResponseModal(true)
      setOpenModal(false)
    }
  }

  const { company } = useCurrentSession()

  const shouldSkipQuery = skipQuery && !openModal
  const { loading: queryLoading, data } = useQuery(BankOfHoursBreakdownPanelDocument, {
    variables: {
      companyId: company.id,
    },
    skip: shouldSkipQuery,
  })

  const reimbursements = data?.compensations.reimbursablePayrollConceptsConnection.nodes ?? []
  const timeRangeCategories = data?.timeSettings.customTimeRangeCategoriesConnection.nodes ?? []
  const payrollConcepts = data?.payroll.conceptsConnection.nodes ?? []

  if (!hasCompensationFlowV2 && !timeBlocksBalance) {
    return null
  }

  const handleCloseResponseModal = () => {
    setOpenResponseModal(false)
    if (onClose) {
      onClose()
    }
  }

  return (
    <>
      {openModal && !hasCompensations && (
        <CreateTimeBlockSupplementWizard
          employeeId={employeeId}
          getResponseData={getResponseData}
          loading={isLoading ?? false}
          onCancel={() => setOpenModal(false)}
          timeBlocksBalance={!hasCompensationFlowV2 ? timeBlocksBalance : undefined}
          timeSheetDate={timeSheetDate}
          selectedCompensationBlocks={hasCompensationFlowV2 ? selectedCompensationBlocks : []}
        />
      )}
      {openModal && hasCompensations && (
        <CreateTimeBlockCompensationWizard
          employeeId={employeeId}
          getResponseData={getResponseData}
          loading={isLoading || queryLoading}
          onCancel={() => setOpenModal(false)}
          timeBlocksBalance={!hasCompensationFlowV2 ? timeBlocksBalance : undefined}
          timeSheetDate={timeSheetDate}
          reimbursements={reimbursements}
          timeRangeCategories={timeRangeCategories}
          payrollConcepts={payrollConcepts}
          selectedCompensationBlocks={hasCompensationFlowV2 ? selectedCompensationBlocks : []}
        />
      )}

      {openResponseModal && (
        <CompensationResponse response={responseData} onCloseModal={handleCloseResponseModal} />
      )}
    </>
  )
}

export default CompensationModalWrapper
